import { DotsVerticalIcon } from "@heroicons/react/solid";
import { CollapsibleSection } from "components/collapsibleSection";
import { DropdownMenu } from "components/dropdownMenu";
import { IAttendantTemplateForm, IAttendantTemplateItem, ISimpleEventDetail } from "interfaces";
import { get, isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { DEFAULT_ATTRACTION_TEMPLATE_FIELDS, DEFAULT_PAGE_SIZE } from "utills/constants";
import { TemplateNameModal } from "../templateNameModal";
import Button from "components/button";
import { GroupForm } from "../groupForm";
import { AddTemplateModal } from "../addTemplateModal";
import { useCreate, useUpdate } from "@refinedev/core";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import Pagination from "components/pagination";
import { paginate } from "utills/helpers";
import { HistoryLodModal } from "components/historyLogModal";

interface ITemplateFields extends IAttendantTemplateItem {
  field_id: string;
}

const INITIAL_TEMPLATE_NAME_CONFIG = {
  open: false,
  defaultValue: "",
  submit: (data: string) => {},
};

interface TemplatesFormProps {
  _createTemplate: (data: IAttendantTemplateItem[], callbackMessage?: string) => void;
  detail: IAttendantTemplateForm;
  refetchTemplates: () => void;
  eventDetailData: ISimpleEventDetail;
}

export const TemplatesForm = ({
  _createTemplate,
  detail,
  eventDetailData,
  refetchTemplates,
}: TemplatesFormProps) => {
  const [templateNameConfig, setTemplateNameConfig] = useState(
    INITIAL_TEMPLATE_NAME_CONFIG
  );
  const [openAddTemplateModal, setOpenAddTemplateModal] = useState(false);
  const [templatePageNumber, setTemplatePageNumber] = useState(1);
  const [openLogModalId, setOpenLogModalId] = useState<string | null>(null);

  const { isLoading: isLoading } = useCreate();
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const {
    watch,
    control,
    trigger,
    getValues,
    resetField,
    formState: { errors },
  } = useFormContext<IAttendantTemplateForm>(); // retrieve all hook methods

  const [openTemplateId, setOpenTemplateId] = useState<string | null | undefined>(null);

  const { fields, replace } = useFieldArray({
    control,
    name: "templates",
    keyName: "field_id"
  });

  useEffect(() => {
    resetField("templates", {defaultValue: detail.templates})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.templates]);

  const _submit = (templateIndex: number) => {
    trigger(`templates.${templateIndex}`).then((isValid) => {
      if (isValid) {
        const templateData = getValues(`templates.${templateIndex}`);
        _updateTemplate(templateData);
      }
    });
  };

  const _updateTemplate = (data: IAttendantTemplateItem, callback?: () => void, callbackMessage?: string) => {
    updateMutation(
      {
        resource: "content/attendant-templates",
        id: data.id,
        meta: {
          URLSuffix: "update",
          ignoreResourceWrapper: true,
        },
        values: {
          templates: {
            attendant_template_name: data.attendant_template_name ?? "",
            data: data.data ?? [],
          },
          event_id: detail.event_id ?? "",
          languages: detail.languages,
        },
      },
      {
        onSuccess: () => {
          toast.success(callbackMessage ?? "Update template successfully");
          refetchTemplates();
          _onCloseModal();
          callback?.();
        },
      }
    );
  };

  const _onCloseModal = () => {
    setTemplateNameConfig(INITIAL_TEMPLATE_NAME_CONFIG);
  };

  const templatePaginationIndex = {
    start: (templatePageNumber - 1) * DEFAULT_PAGE_SIZE,
    end: (templatePageNumber * DEFAULT_PAGE_SIZE) - 1,
  }
  
  return (
    <>
      <div className="box-container p-0">
        <div className="flex flex-col flex-wrap gap-y-3 border-b border-gray-200 py-4 px-4 sm:px-6 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="flex-1 shrink-0 text-base font-semibold leading-6">
            Participant’s Templates
          </h3>
        </div>
        {/* <div className="box-container">
          <p>Do you want to use templates from your other events?</p>
          <Button
            className="w-fit mt-4 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
            onClick={() => setOpenAddTemplateModal(true)}
          >
            Select other templates
          </Button>
        </div> */}
      </div>
      <Button
        className="w-full bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100 mb-2"
        onClick={() =>
          setTemplateNameConfig({
            open: true,
            defaultValue: "",
            submit: (templateName: string) =>
              _createTemplate([
                {
                  attendant_template_name: templateName,
                  data: [...DEFAULT_ATTRACTION_TEMPLATE_FIELDS],
                },
              ]),
          })
        }
      >
        Add more Templates
      </Button>

      <div>
        <div>
          <div className="grid sm:grid-cols-6 lg:gap-x-6">
            <div className="col-span-full">
              {fields.map((field: ITemplateFields, index: number) => (index >= templatePaginationIndex.start && index <= templatePaginationIndex.end) ? ( // only show item in range
                <Controller
                  name={`templates.${index}`}
                  control={control}
                  defaultValue={field}
                  key={field.field_id}
                  render={({ field: { onChange, value, ref } }) => (
                    <div
                      className={`border rounded-lg mb-4 sm:mb-6 ${
                        get(errors, `templates.${index}`, "")
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      key={field.field_id}
                    >
                      <CollapsibleSection
                        label={
                          watch("templates")[index].attendant_template_name
                        }
                        customOpen={{
                          open: openTemplateId === field.id,
                          onClick: () => setOpenTemplateId(prevId => prevId === field.id ? null : field.id)
                        }}
                        isAnimated={false}
                        toolbar={
                          <DropdownMenu
                            label={
                              <DotsVerticalIcon className="w-4 h-4 text-brand-500" />
                            }
                            items={[
                              {
                                label: "Duplicate",
                                onClick: () => _createTemplate([field], "Duplicated successfully"),
                              },
                              {
                                label: "Edit",
                                onClick: () =>
                                  setTemplateNameConfig({
                                    open: true,
                                    defaultValue:
                                      value.attendant_template_name ?? "",
                                    submit: (templateName: string) => {
                                      onChange({
                                        ...detail.templates[index],
                                        attendant_template_name: templateName,
                                      });
                                      _submit(index);
                                    },
                                  }),
                              },
                              {
                                label: "Log",
                                onClick: () => setOpenLogModalId(value.id ?? ""),
                              },
                            ]}
                          />
                        }
                      >
                        <GroupForm
                          templateIndex={index}
                          eventDetailData={eventDetailData}
                          onSubmit={_updateTemplate}
                        />
                      </CollapsibleSection>
                    </div>
                  )}
                />
              ) : <></>)}
            </div>
          </div>
          <div className="text-center text-sm">
            {isEmpty(fields) ? <p>No Template</p> : <></>}
          </div>
          {fields.length > DEFAULT_PAGE_SIZE ? (
            <div className="col-span-full bg-white p-4 sm:p-6">
              <Pagination
                currentPage={templatePageNumber}
                totalPage={Math.ceil(fields.length / DEFAULT_PAGE_SIZE)}
                hideTotal
                total={{
                  totalItems: fields.length || 0,
                  total: 0,
                }}
                onChange={(page) => setTemplatePageNumber(page)}
              />
            </div>
          ) : <></>}
        </div>
        <TemplateNameModal
          key="template-name"
          defaultValue={templateNameConfig.defaultValue}
          onClose={_onCloseModal}
          open={templateNameConfig.open}
          onSubmit={templateNameConfig.submit}
        />
      </div>
      <AddTemplateModal
        id={eventDetailData.id}
        onClose={() => setOpenAddTemplateModal(false)}
        open={openAddTemplateModal}
        onAddTempate={(data) => _createTemplate(data)}
      />
      <HistoryLodModal
        resource={`content/attendant-templates/${openLogModalId}/history`}
        open={openLogModalId ? true : false}
        onClose={() => setOpenLogModalId(null)}
        key="attendant-template"
        id="attendant-template"
      />
      {isLoading || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </>
  );
};
